import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, CreditCard, FileDown, Loader2, Clock, Landmark } from "lucide-react";
import { http, API, formatApiError } from "../lib/api";

const eur = (n) => Number(n || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
const fr = (s) => (s ? s.slice(0, 10).split("-").reverse().join("/") : "");

const Row = ({ l, v, b, testid }) => (
  <div className={`flex justify-between py-1.5 ${b ? "font-display text-lg font-bold border-t border-slate-200 mt-1 pt-3" : "text-sm text-slate-600"}`}><span>{l}</span><span data-testid={testid}>{v}</span></div>
);

const METHOD_LABEL = { "card,sepa_debit": "par carte ou prélèvement SEPA", card: "par carte bancaire", sepa_debit: "par prélèvement SEPA" };

const MandateBox = ({ token, mandate }) => {
  const [qs] = useSearchParams();
  const [state, setState] = useState(mandate.active ? "on" : "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    const ms = qs.get("mandate_session");
    if (ms) http.get(`/public/xaltix-pay/${token}/mandate-status`, { params: { session_id: ms } }).then((r) => r.data.active && setState("on")).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const setup = async () => {
    setBusy(true); setErr("");
    try { const { data } = await http.post(`/public/xaltix-pay/${token}/mandate`, { origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { setErr(formatApiError(e)); setBusy(false); }
  };
  return (
    <div className="mt-5 rounded-md border border-slate-200 p-4" data-testid="mandate-box">
      <p className="font-semibold text-sm flex items-center gap-2"><Landmark className="h-4 w-4" />Prélèvement automatique</p>
      {state === "on" ? <p className="text-sm text-emerald-700 mt-1" data-testid="mandate-active">Activé{mandate.last4 ? ` (IBAN •••• ${mandate.last4})` : ""} : vos prochaines factures seront prélevées {mandate.days} jours avant leur échéance.</p> : (
        <>
          <p className="text-xs text-slate-600 mt-1">Enregistrez un mandat SEPA : chaque nouvelle facture sera prélevée automatiquement {mandate.days} jours avant son échéance, sans risque de suspension.</p>
          <button onClick={setup} disabled={busy} className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#0B2A7A] text-[#0B2A7A] text-sm font-semibold hover:bg-[#0B2A7A] hover:text-white transition-colors disabled:opacity-50" data-testid="mandate-setup-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Landmark className="h-4 w-4" />}Activer le prélèvement automatique</button>
          {err && <p className="text-xs text-rose-700 mt-1" data-testid="mandate-error">{err}</p>}
        </>
      )}
    </div>
  );
};

export default function PayXaltixInvoice() {
  const { token } = useParams();
  const [qs] = useSearchParams();
  const sid = qs.get("session_id");
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [poll, setPoll] = useState(sid ? "checking" : "");
  const load = () => http.get(`/public/xaltix-pay/${token}`).then((r) => setD(r.data)).catch((e) => setErr(formatApiError(e)));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!sid) return;
    let n = 0, stop = false;
    const tick = async () => {
      try {
        const { data } = await http.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setPoll("paid"); load(); return; }
        if (data.status === "processing") { setPoll("processing"); load(); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setPoll("failed"); return; }
      } catch { /* retry */ }
      if (!stop && ++n < 20) setTimeout(tick, 2000); else setPoll("pending");
    };
    tick();
    return () => { stop = true; };
  }, [sid]); // eslint-disable-line react-hooks/exhaustive-deps
  const pay = async () => {
    setBusy(true); setErr("");
    try { const { data } = await http.post(`/public/xaltix-pay/${token}/checkout`, { origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { setErr(formatApiError(e)); setBusy(false); }
  };
  const inv = d?.invoice;
  const paid = inv?.status === "paid" || poll === "paid";
  const processing = !paid && (inv?.processing || poll === "processing");
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-white border border-slate-200 rounded-xl shadow-sm p-7" data-testid="pay-page">
        <p className="text-[11px] font-bold uppercase tracking-[.2em] text-[#C9962E]">XALTIX par Solutions PRIME</p>
        <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1">Paiement de facture</h1>
        {err && !d && <p className="mt-6 text-rose-700" data-testid="pay-error">{err}</p>}
        {inv && (
          <>
            <p className="text-sm text-slate-600 mt-2" data-testid="pay-invoice-number">Facture n° <b>{inv.number}</b> · {inv.client} · émise le {fr(inv.issue_date)}{inv.due_date ? ` · échéance ${fr(inv.due_date)}` : ""}</p>
            {inv.period_start && <p className="text-xs text-slate-500">Période du {fr(inv.period_start)} au {fr(inv.period_end)}</p>}
            <div className="mt-5">
              <Row b l="Montant à payer TTC" v={eur(inv.total_ttc)} testid="pay-total" />
              {!paid && inv.deadline && <p className="text-xs text-slate-500 mt-1" data-testid="pay-deadline">À régler au plus tard le {fr(inv.deadline)} pour conserver l'accès à vos fonctionnalités.</p>}
            </div>
            {paid && <div className="mt-5 flex items-center gap-2 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 p-3 text-sm" data-testid="pay-success"><CheckCircle2 className="h-5 w-5" />Facture réglée. Merci ! Votre accès complet à XALTIX est actif.</div>}
            {processing && <div className="mt-5 flex items-center gap-2 rounded-md bg-sky-50 border border-sky-200 text-sky-900 p-3 text-sm" data-testid="pay-processing"><Clock className="h-5 w-5" />Prélèvement SEPA en cours de traitement (quelques jours ouvrés). Vous serez informé dès sa confirmation.</div>}
            {poll === "checking" && <p className="mt-5 flex items-center gap-2 text-sm text-slate-600" data-testid="pay-checking"><Loader2 className="h-4 w-4 animate-spin" />Vérification du paiement…</p>}
            {poll === "failed" && <p className="mt-5 text-sm text-rose-700" data-testid="pay-failed">Le paiement n'a pas abouti. Vous pouvez réessayer.</p>}
            {qs.get("cancel") && !paid && <p className="mt-5 text-sm text-amber-700" data-testid="pay-cancelled">Paiement annulé : aucun montant n'a été débité.</p>}
            {err && <p className="mt-4 text-sm text-rose-700" data-testid="pay-error">{err}</p>}
            {!paid && !processing && poll !== "checking" && (
              <button onClick={pay} disabled={busy} className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0B2A7A] text-white font-semibold py-3 hover:bg-[#0a2365] transition-colors disabled:opacity-60" data-testid="pay-submit-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}Payer {eur(inv.total_ttc)} {METHOD_LABEL[(d.methods || []).join(",")] || "en ligne"}</button>
            )}
            {d.mandate?.enabled && <MandateBox token={token} mandate={d.mandate} />}
            <a href={`${API}/public/xaltix-pay/${token}/pdf`} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-sky-700" data-testid="pay-pdf-link"><FileDown className="h-4 w-4" />Télécharger la facture (PDF)</a>
            <p className="mt-6 text-[11px] text-slate-400">Paiement sécurisé par Stripe. {d.seller?.name} · {d.seller?.email}</p>
          </>
        )}
      </div>
    </div>
  );
}
